import undoable, {excludeAction} from "redux-undo";
import {IItem} from "../interfaces/Item";
import {ILabel} from "../interfaces/Label";
import {calcPxInMM, defaultPath, orientation} from "../utils/constants";
import {BARCODE, PICTURE, VARIABLE_PICTURE} from "../utils/ItemTypes";
import {ILLTag, ITag} from "../interfaces/Tag";
import {IDpi} from "../interfaces/Dpi";
import { barcodeTagList, dpiValues, labelSizes, stringTagList } from "../utils/defaultValues";
import { ILabelSize } from "../interfaces/ILabelSize";
import { IEmployee } from "../interfaces/Employee";

// split state: only labelItems and labelParams need undo/redo
interface LabelEditState {
  labelItems: IItem[];
  labelParams: ILabel;
}

interface ReferenceDataState {
  recid: number;
  templateName: string;
  barcodeTags: ITag[];
  imgTags?: ITag[];
  textTags: ITag[];
  techTags?: ITag[];
  dpiValues: IDpi[];
  labelSizes: ILabelSize[];
  allTags: ILLTag[];
  employees: IEmployee[];
  templates?: any[];
}

export const initialLabelEditState: LabelEditState = {
  labelParams: {
    sizeInMM: '105x148',
    orientation: orientation.portrait,
    dpi: 96,
    zoom: 1,
    widthInPx: 105 * calcPxInMM(96),
    heightInPx: 148 * calcPxInMM(96),
    pxInMM: calcPxInMM(96),
    showDefaultValues: true,
    defaultPath: defaultPath,
    showGrid: false,
    gridCellWidth: 0,
    selectedLabelSizeId: 0,
  },
  labelItems: [],
};

export const initialReferenceDataState: ReferenceDataState = {
  recid: -1,
  barcodeTags: barcodeTagList,
  imgTags: [],
  textTags: stringTagList,
  techTags: [],
  allTags: [],
  dpiValues: dpiValues,
  labelSizes: labelSizes,
  employees: [],
  templates: [],
  templateName: ""
};

export interface LabelEditAction {
  type:
    | typeof SET_SELECTED_ITEM
    | typeof ADD_ITEM_TO_LABEL
    | typeof SET_COORDINATES
    | typeof SET_COORDINATES_NO_HISTORY
    | typeof CHANGE_VALUE
    | typeof CHANGE_LABEL_PARAMS
    | typeof REMOVE_ITEM_FROM_LABEL
    | typeof IMPORT_XML
    | typeof CHANGE_LINE_VALUE
    | typeof CHANGE_OBJECT_RESIZE
    | typeof CHANGE_BARCODE_RESIZE
    | typeof CHANGE_VALUE_WITH_STYLE
    | typeof CHANGE_BARCODE_VALUE
    | typeof CHANGE_VALUE_NO_HISTORY
    | typeof CHANGE_PICTURE
    | typeof NEW_LABEL
    | typeof CHANGE_BARCODE_NO_HISTORY
    | typeof REMOVE_ALL_SELECTION
    | typeof CLEAR_ALL_ITEMS
    | typeof SET_ZOOM
    | typeof LOGOUT;
  item?: IItem;
  x?: number;
  y?: number;
  propName?: string;
  propValue?: any;
  labelParams?: ILabel;
  labelItems?: IItem[];
  xTo?: number;
  yTo?: number;
  width?: number;
  height?: number;
  value?: string;
  propNameStyle?: string;
  propValueStyle?: any;
  path?: string;
  zoom?: number;
}

export interface ReferenceDataAction {
  type:
    | typeof SET_TAGS
    | typeof SET_DPI
    | typeof SET_LABEL_SIZES
    | typeof SET_EMPLOYEES
    | typeof SET_RECID
    | typeof SET_TEMPLATES
    | typeof LOGOUT
    | typeof LOGOUT_WITHOUT_CLEAR;
  dpiValues?: IDpi[];
  barcodeTags?: ITag[];
  imgTags?: ITag[];
  textTags?: ITag[];
  techTags?: ITag[];
  labelSizes?: ILabelSize[];
  allTags?: ILLTag[];
  employees?: IEmployee[];
  recid?: number;
  templates?: any[];
  templateName?: string;
}

// Label editing reducer - only handles labelItems and labelParams
const LabelEditReducer = (state: LabelEditState = initialLabelEditState, action: LabelEditAction): LabelEditState => {
  switch (action.type) {
    case NEW_LABEL:
      return {
        ...state,
        labelItems: [],
        labelParams: {...initialLabelEditState.labelParams}
      };
    case CLEAR_ALL_ITEMS:
      return {
        ...state,
        labelItems: []
      };
    case SET_SELECTED_ITEM:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid) {
            return {...item, selected: true};
          } else {
            return {...item, selected: false};
          }
        }),
      };
    case ADD_ITEM_TO_LABEL:
      return {
        ...state,
        labelItems: action.item ? [...state.labelItems, action.item] : [...state.labelItems],
      };
    case REMOVE_ITEM_FROM_LABEL:
      return {
        ...state,
        labelItems: state.labelItems.length > 1 ? state.labelItems.filter((item) => !item.selected) : [],
      };
    case SET_COORDINATES:
    case SET_COORDINATES_NO_HISTORY:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && item.selected === true) {
            return {
              ...item,
              xInMM: Number(action.x) * (1 / state.labelParams.pxInMM),
              yInMM: Number(action.y) * (1 / state.labelParams.pxInMM),
              style: {
                ...item.style,
                top: `${action.y}px`,
                left: `${action.x}px`,
              },
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_VALUE:
    case CHANGE_VALUE_NO_HISTORY:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && item.selected === true) {
            return {
              ...item,
              [action.propName as string]: action.propValue,
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_BARCODE_NO_HISTORY:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid) {
            return {
              ...item,
              [action.propName as string]: action.propValue,
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_PICTURE:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && item.selected === true && (item.type === PICTURE || item.type === VARIABLE_PICTURE)) {
            return {
              ...item,
              value: action.value as string,
              path: action.path,
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_VALUE_WITH_STYLE:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && item.selected === true) {
            return {
              ...item,
              [action.propName as string]: action.propValue,
              style: {...item.style, [action.propNameStyle as string]: action.propValueStyle},
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_OBJECT_RESIZE:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && item.selected === true) {
            return {
              ...item,
              width: action.width,
              [action.propName as string]: action.propValue,
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_BARCODE_RESIZE:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && item.selected === true && item.type === BARCODE) {
            return {
              ...item,
              width: action.width,
              height: action.height,
              value: action.value as string,
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_BARCODE_VALUE:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && item.selected === true && item.type === BARCODE) {
            return {
              ...item,
              [action.propName as string]: action.propValue,
              value: action.value as string,
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_LINE_VALUE:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          if (item.itemid === action.item?.itemid && action.item.selected === true) {
            return {
              ...item,
              xInMM: Number(action.x),
              yInMM: Number(action.y),
              xToInMM: Number(action.xTo),
              yToInMM: Number(action.yTo),
              style: {
                ...item.style,
                top: `${action.y! * state.labelParams.pxInMM}px`,
                left: `${action.x! * state.labelParams.pxInMM}px`,
              },
            };
          } else {
            return item;
          }
        }),
      };
    case CHANGE_LABEL_PARAMS:
      if (action.propName === "dpi") {
        const newPxInMM = calcPxInMM(action.propValue || 200);
        return {
          ...state,
          labelParams: {...state.labelParams, [action.propName as string]: action.propValue, pxInMM: newPxInMM},
          labelItems: state.labelItems.map((item) => ({
            ...item,
            style: {
              ...item.style,
              top: `${item.yInMM * newPxInMM}px`,
              left: `${item.xInMM * newPxInMM}px`,
            },
          })),
        };
      } else {
        return {
          ...state,
          labelParams: {...state.labelParams, [action.propName as string]: action.propValue},
        };
      }
    case IMPORT_XML:
      return {
        ...state,
        labelParams: action.labelParams!,
        labelItems: action.labelItems || [],
      };
    case REMOVE_ALL_SELECTION:
      return {
        ...state,
        labelItems: state.labelItems.map((item) => {
          return {
            ...item,
            selected: false,
          };
        }),
      };
    case SET_ZOOM:
      return {
        ...state,
        labelParams: {...state.labelParams, zoom: action.zoom || 1},
      };
    case LOGOUT:
      return initialLabelEditState;
    default:
      return state;
  }
};

const ReferenceDataReducer = (state: ReferenceDataState = initialReferenceDataState, action: ReferenceDataAction): ReferenceDataState => {
  switch (action.type) {
    case SET_TAGS:
      return {
        ...state,
        barcodeTags: action.barcodeTags || [],
        imgTags: action.imgTags || [],
        textTags: action.textTags || [],
        techTags: action.techTags || [],
        allTags: action.allTags || [],
      };
    case SET_DPI:
      return {
        ...state,
        dpiValues: action.dpiValues || [],
      };
    case SET_LABEL_SIZES:
      return {
        ...state,
        labelSizes: action.labelSizes || [],
      };
    case SET_EMPLOYEES:
      return {
        ...state,
        employees: action.employees || [],
      };
    case SET_RECID:
      return {
        ...state,
        recid: action.recid || -1,
        templateName: action.templateName || ""
      };
    case SET_TEMPLATES:
      return {
        ...state,
        templates: action.templates || [],
      };
    case LOGOUT:
      return initialReferenceDataState;
    case LOGOUT_WITHOUT_CLEAR:
      return {
        ...state,
        barcodeTags: barcodeTagList,
        imgTags: [],
        textTags: stringTagList,
        techTags: [],
        allTags: [],
        dpiValues: dpiValues,
        labelSizes: labelSizes,
        employees: [],
        templates: [],
      };
    default:
      return state;
  }
};

export const SET_SELECTED_ITEM = "SET_SELECTED_ITEM";
export const ADD_ITEM_TO_LABEL = "ADD_ITEM_TO_LABEL";
export const SET_COORDINATES = "SET_COORDINATES";
export const SET_COORDINATES_NO_HISTORY = "SET_COORDINATES_NO_HISTORY";
export const CHANGE_VALUE = "CHANGE_VALUE";
export const CHANGE_LABEL_PARAMS = "CHANGE_LABEL_PARAMS";
export const REMOVE_ITEM_FROM_LABEL = "REMOVE_ITEM_FROM_LABEL";
export const IMPORT_XML = "IMPORT_XML";
export const CHANGE_LINE_VALUE = "CHANGE_LINE_VALUE";
export const CHANGE_OBJECT_RESIZE = "CHANGE_OBJECT_RESIZE";
export const CHANGE_BARCODE_RESIZE = "CHANGE_BARCODE_RESIZE";
export const CHANGE_VALUE_WITH_STYLE = "CHANGE_VALUE_WITH_STYLE";
export const CHANGE_BARCODE_VALUE = "CHANGE_BARCODE_VALUE";
export const CHANGE_VALUE_NO_HISTORY = "CHANGE_VALUE_NO_HISTORY";
export const CHANGE_PICTURE = "CHANGE_PICTURE";
export const NEW_LABEL = "NEW_LABEL";
export const CLEAR_ALL_ITEMS = "CLEAR_ALL_ITEMS";
export const CHANGE_BARCODE_NO_HISTORY = "CHANGE_BARCODE_NO_HISTORY";
export const REMOVE_ALL_SELECTION = "REMOVE_ALL_SELECTION";
export const SET_ZOOM = "SET_ZOOM";
export const LOGOUT = "LOGOUT";
export const SET_TAGS = "SET_TAGS";
export const SET_DPI = "SET_DPI";
export const SET_LABEL_SIZES = "SET_LABEL_SIZES";
export const SET_EMPLOYEES = "SET_EMPLOYEES";
export const SET_RECID = "SET_RECID";
export const SET_TEMPLATES = "SET_TEMPLATES";
export const LOGOUT_WITHOUT_CLEAR = "LOGOUT_WITHOUT_CLEAR";

const undoableLabelEdit = undoable(LabelEditReducer, {
  limit: 20,
  // debug: true,
  filter: excludeAction([CHANGE_LABEL_PARAMS, CHANGE_VALUE_NO_HISTORY, CHANGE_BARCODE_NO_HISTORY, SET_COORDINATES_NO_HISTORY, SET_ZOOM, LOGOUT]),
});

export { undoableLabelEdit, ReferenceDataReducer };
